import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { buildMonthlyChartRows } from "./pygRealPptoUtils";

const fmtAxis = (v) => {
  if (typeof v !== "number" || Number.isNaN(v)) return v;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
};

const chartPaperSx = (theme) => ({
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.85),
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 6px 18px rgba(0,0,0,0.28)"
      : "0 6px 16px rgba(15,23,42,0.06)",
  height: "100%",
  minWidth: 0,
});

const ChartTooltip = ({ active, payload, label, formatearMiles }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.25 }} elevation={4}>
      <Typography variant="caption" sx={{ fontWeight: 800, display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.name}
          variant="caption"
          display="block"
          sx={{ color: entry.color }}
        >
          {entry.name}: {formatearMiles(entry.value ?? 0)}
        </Typography>
      ))}
    </Paper>
  );
};

const RealPptoLinePanel = ({
  title,
  subtitle,
  data,
  formatearMiles,
  mode,
  height = 260,
}) => {
  const theme = useTheme();
  const colorReal = theme.palette.info.main;
  const colorPpto = theme.palette.error.main;
  const gridStroke = alpha(theme.palette.divider, 0.9);
  const isAcum = mode === "acum";

  return (
    <Paper sx={chartPaperSx(theme)}>
      <Typography variant="subtitle2" fontWeight={800} gutterBottom>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={52} />
          <Tooltip content={<ChartTooltip formatearMiles={formatearMiles} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
          <Line
            type="monotone"
            dataKey={isAcum ? "acumReal" : "real"}
            name={isAcum ? "Acum. Real" : "Real"}
            stroke={colorReal}
            strokeWidth={2.5}
            connectNulls
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey={isAcum ? "acumPpto" : "ppto"}
            name={isAcum ? "Acum. Ppto" : "Ppto"}
            stroke={colorPpto}
            strokeWidth={isAcum ? 2 : 2.5}
            strokeDasharray={isAcum ? "6 4" : undefined}
            connectNulls
            dot={{ r: isAcum ? 2 : 3 }}
            activeDot={{ r: isAcum ? 4 : 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

const PygEpigrafesRealPptoChart = ({ epigrafes = [], formatearMiles }) => {
  const [epigrafeId, setEpigrafeId] = useState(() => epigrafes[0]?.id ?? "");

  useEffect(() => {
    if (!epigrafes.length) return;
    const exists = epigrafes.some((e) => e.id === epigrafeId);
    if (!exists) setEpigrafeId(epigrafes[0].id);
  }, [epigrafes, epigrafeId]);

  const selectedEpigrafe = useMemo(
    () => epigrafes.find((e) => e.id === epigrafeId) ?? epigrafes[0],
    [epigrafes, epigrafeId],
  );

  const chartData = useMemo(
    () => buildMonthlyChartRows(selectedEpigrafe?.serie),
    [selectedEpigrafe],
  );

  const epigrafeLabel = selectedEpigrafe?.label ?? "";

  if (!epigrafes.length || !chartData.length) return null;

  const totReal = selectedEpigrafe?.serie?.total?.real ?? 0;
  const totPpto = selectedEpigrafe?.serie?.total?.estimate ?? 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.25 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            Epígrafes clave: Real vs presupuesto
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {epigrafeLabel} — Total anual Real: {formatearMiles(totReal)} · Ppto:{" "}
            {formatearMiles(totPpto)}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 280 } }}>
          <InputLabel>Epígrafe</InputLabel>
          <Select
            label="Epígrafe"
            value={selectedEpigrafe?.id ?? ""}
            onChange={(e) => setEpigrafeId(e.target.value)}
          >
            {epigrafes.map((ep) => (
              <MenuItem key={ep.id} value={ep.id}>
                {ep.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}>
          <RealPptoLinePanel
            title={`${epigrafeLabel}: Real vs presupuesto por mes`}
            subtitle="Importe de cada mes (no acumulado)"
            data={chartData}
            formatearMiles={formatearMiles}
            mode="mensual"
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <RealPptoLinePanel
            title={`${epigrafeLabel}: acumulado Real vs presupuesto`}
            subtitle="Suma progresiva mes a mes; en M12 coincide con el total anual"
            data={chartData}
            formatearMiles={formatearMiles}
            mode="acum"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PygEpigrafesRealPptoChart;
